import React from 'react';
import { useTranslation } from 'react-i18next';
import { Workflow, GitBranch, Columns3, Network } from 'lucide-react';
import {
  StartPickerShell,
  StartPickerGenerateButton,
  StartPickerCard,
  StartPickerBlankCard,
} from '../../../components/start-picker/StartPicker';

/**
 * Flowchart Start picker — the per-type start experience, built from the
 * shared start-picker primitives (components/start-picker). Shown once over
 * a freshly-created, empty flowchart with no pre-chosen seed. Pick Blank, a
 * starter (each hydrated by a Seed Registry provider), or Generate-from-text.
 */

export interface FlowchartStartPickerProps {
  /** Fires with a Seed Registry id ('flowchart-basic' | 'swimlane-approval' | 'bpmn-order'). */
  onSelectStarter: (seedId: string) => void;
  onStartBlank: () => void;
  onGenerateFromText: () => void;
}

const STARTERS = [
  {
    id: 'flowchart-basic',
    nameKey: 'startPicker.starters.basic.name',
    descriptionKey: 'startPicker.starters.basic.description',
    icon: <Workflow className="size-6" />,
  },
  {
    id: 'swimlane-approval',
    nameKey: 'startPicker.starters.swimlane.name',
    descriptionKey: 'startPicker.starters.swimlane.description',
    icon: <Columns3 className="size-6" />,
  },
  {
    id: 'bpmn-order',
    nameKey: 'startPicker.starters.bpmn.name',
    descriptionKey: 'startPicker.starters.bpmn.description',
    icon: <Network className="size-6" />,
  },
];

export const FlowchartStartPicker: React.FC<FlowchartStartPickerProps> = ({
  onSelectStarter,
  onStartBlank,
  onGenerateFromText,
}) => {
  const { t } = useTranslation('process');
  return (
    <StartPickerShell
      icon={<GitBranch className="size-5" />}
      title={t('startPicker.title')}
      subtitle={t('startPicker.subtitle')}
      testId="tid.onboarding.flowchartStartPicker"
    >
      <StartPickerGenerateButton
        title={t('startPicker.generateTitle')}
        description={t('startPicker.generateDescription')}
        onClick={onGenerateFromText}
        testId="tid.onboarding.flowchartStartPicker.generate"
      />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {STARTERS.map((s) => (
          <StartPickerCard
            key={s.id}
            icon={s.icon}
            name={t(s.nameKey) as string}
            description={t(s.descriptionKey) as string}
            onClick={() => onSelectStarter(s.id)}
            testId={`tid.onboarding.flowchartStartPicker.${s.id}`}
          />
        ))}
        <StartPickerBlankCard
          name={t('startPicker.blank.name')}
          description={t('startPicker.blank.description')}
          onClick={onStartBlank}
          testId="tid.onboarding.flowchartStartPicker.blank"
        />
      </div>
    </StartPickerShell>
  );
};
